// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The gate.
 *
 * It answers one question — WILL THE READER YOU CARE ABOUT SEE THIS? — and it
 * answers it by re-reading the bytes that were just written, never by trusting
 * that the writer did what it was asked.
 *
 * `predictComfyReadable` is an EMULATION of ComfyUI's front-end reader, written
 * from `Comfy-Org/ComfyUI_frontend` `src/scripts/metadata/png.ts` and
 * `src/scripts/metadata/parser.ts` as they stood on 2026-08-23. It is a
 * PREDICTION and the interface labels it as one, with that date. It is
 * deliberately faithful to that reader's quirks rather than being nicer than
 * it, because a gate that is more forgiving than the real thing is worse than
 * no gate:
 *
 *   - It branches on `tEXt | comf | iTXt` only. There is NO zTXt branch, so a
 *     zTXt chunk is invisible to it and no error is raised.
 *   - It never checks a CRC, so it reads chunks a strict reader would reject.
 *   - Its loop is `while (offset < pngData.length)` with no IEND stop, so it
 *     walks into whatever follows IEND.
 *   - And that is a real bug worth reporting: with 1 to 3 bytes after IEND, the
 *     `dataView.getUint32(offset)` at the top of the loop throws an uncaught
 *     RangeError and the whole read rejects — so a file that plainly contains a
 *     workflow loads as nothing at all.
 *   - Its dispatch is on the FILE'S MIME TYPE, not on the bytes: `parser.ts`
 *     tests `file.type === 'image/png'`. A perfectly good PNG saved under the
 *     wrong extension is never even opened.
 */

import { parseTextChunkLayout, latin1Decode, utf8Decode } from '../png/text.ts';
import { inflateZlib } from '../png/zlib.ts';
import type { ComfyPrediction, PngParse, ResidualRow } from '../types.ts';

export const COMFY_READER_SOURCE =
  'Comfy-Org/ComfyUI_frontend — src/scripts/metadata/png.ts';
export const COMFY_READER_DATE = '2026-08-23';

const COMFY_TYPES = new Set(['tEXt', 'comf', 'iTXt']);

function readUint32(bytes: Uint8Array, at: number): number {
  return ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;
}

export async function predictComfyReadable(
  bytes: Uint8Array,
  filename: string,
): Promise<ComfyPrediction> {
  const notes: string[] = [];

  if (!/\.png$/i.test(filename)) {
    return {
      completed: false,
      sawKeys: [],
      notes: [
        `ComfyUI decides what to do with a dropped file from its MIME type rather than its bytes, and under the name “${filename}” the browser will not report it as image/png. Rename it to end in .png and it will be read.`,
      ],
    };
  }

  if (bytes.length < 8 || readUint32(bytes, 0) !== 0x89504e47) {
    return { completed: false, sawKeys: [], notes: ['The file does not begin with a PNG signature, so the reader rejects it immediately.'] };
  }

  const keys: string[] = [];
  let offset = 8;
  let completed = true;

  while (offset < bytes.length) {
    // The real reader reads four bytes here with no bounds check.
    if (offset + 4 > bytes.length) {
      completed = false;
      notes.push(
        `There are ${bytes.length - offset} stray bytes after the end of the image. ComfyUI’s reader keeps walking past IEND and reads four bytes at a time, so it throws on those and loads nothing at all — not even the chunks it had already found.`,
      );
      break;
    }
    const length = readUint32(bytes, offset);
    let type = '';
    for (let i = 0; i < 4 && offset + 4 + i < bytes.length; i++) {
      type += String.fromCharCode(bytes[offset + 4 + i]);
    }

    if (COMFY_TYPES.has(type) && offset + 8 + length <= bytes.length) {
      const data = bytes.subarray(offset + 8, offset + 8 + length);
      const layout = parseTextChunkLayout(type === 'comf' ? 'tEXt' : type, data);
      if (!layout.error && layout.keyword) {
        let text = '';
        try {
          const payload = layout.compressed ? await inflateZlib(data.subarray(layout.payloadStart)) : data.subarray(layout.payloadStart);
          // The reader decodes every chunk as UTF-8, including tEXt, which the
          // specification says is Latin-1.
          text = utf8Decode(payload);
        } catch {
          text = latin1Decode(data.subarray(layout.payloadStart));
        }
        if (text.length > 0 && !keys.includes(layout.keyword)) keys.push(layout.keyword);
      }
    }
    offset += 12 + length;
  }

  return { completed, sawKeys: keys, notes };
}

/** Explain, in the tool's own words, what that prediction means for the user. */
export function comfyVerdictNotes(prediction: ComfyPrediction, parse: PngParse): string[] {
  const notes = [...prediction.notes];
  const zTxt = parse.chunks.filter((c) => c.type === 'zTXt');
  if (zTxt.length > 0 && !prediction.sawKeys.length) {
    notes.push(
      `${zTxt.length} zTXt chunk${zTxt.length === 1 ? '' : 's'} in this file cannot be seen by ComfyUI at all — its reader tests for tEXt, comf and iTXt and has no branch for zTXt, so it reports nothing rather than an error.`,
    );
  }
  if (prediction.completed && prediction.sawKeys.includes('prompt') && !prediction.sawKeys.includes('workflow')) {
    notes.push(
      'Only the API graph is present. ComfyUI handles that: when a dropped image has no `workflow`, it rebuilds a canvas from `prompt` instead. The layout will not be the one you arranged, but the nodes and their settings will be.',
    );
  }
  return notes;
}

export function residualReport(parse: PngParse): ResidualRow[] {
  const rows = new Map<string, ResidualRow>();
  for (const chunk of parse.chunks) {
    const row = rows.get(chunk.type) ?? { type: chunk.type, count: 0, bytes: 0 };
    row.count++;
    row.bytes += chunk.dataLen + 12;
    rows.set(chunk.type, row);
  }
  if (parse.trailerLength > 0) {
    rows.set('(after IEND)', { type: '(after IEND)', count: 1, bytes: parse.trailerLength });
  }
  return [...rows.values()].sort((a, b) => b.bytes - a.bytes);
}
